import { BodyScrollEndEvent, GridApi, IRowNode } from 'ag-grid-community';
import { startTransition, useCallback, useRef } from 'react';

interface VisibleData {
  symbols: string[];
  fields: string[];
  removedSymbols?: string[];
}

interface useViewportData {
  getVisibleData: (gridApi: GridApi | null) => VisibleData;
  onTransactionApplied: (gridApi: GridApi | null, callback: () => void) => void;
  filterVisibleSymbols: (gridApi: GridApi | null, symbols: string[]) => string[];
  initOnScrollEnd: (
    gridApi: GridApi | null,
    callback: (visibleData: VisibleData, event: BodyScrollEndEvent) => void,
  ) => ((event: BodyScrollEndEvent) => void) | null;
  getRemovedSymbols: (gridApi: GridApi | null) => string[];
}

interface TickerRow {
  symbol?: string;
  ticker?: string;
  data?: { symbol?: string; ticker?: string };
  id?: string;
}

const getTickerSlug = (row: TickerRow | null | undefined) => {
  if (!row) return null;
  return row.symbol ?? row.ticker ?? row.data?.symbol ?? row.data?.ticker ?? row.id ?? null;
};

export const useViewportData = (): useViewportData => {
  const scrollListener = useRef<((event: BodyScrollEndEvent) => void) | null>(null);
  const previouslyRenderedNodes = useRef<IRowNode[]>([]);

  const getVisibleData = useCallback((gridApi: GridApi | null): VisibleData => {
    const visibleRows = gridApi?.getRenderedNodes().map(node => node.data);
    const visibleColumns = gridApi?.getAllDisplayedVirtualColumns();
    const symbols = visibleRows?.map(getTickerSlug)?.filter((s): s is string => !!s) ?? [];
    const fields = (visibleColumns?.map(col => col.getColDef().colId)?.filter(Boolean) as string[]) ?? [];

    return {
      fields,
      symbols,
    };
  }, []);

  const getRemovedSymbols = useCallback((gridApi: GridApi | null): string[] => {
    const currentlyRenderedNodes = gridApi?.getRenderedNodes();
    const currentTickers = currentlyRenderedNodes?.map(node => getTickerSlug(node)) ?? [];

    previouslyRenderedNodes.current = previouslyRenderedNodes?.current?.filter(
      node => !currentTickers.includes(getTickerSlug(node)),
    );

    if (previouslyRenderedNodes?.current?.length) {
      const previousTickers = previouslyRenderedNodes.current.map(node => getTickerSlug(node));
      previouslyRenderedNodes.current = currentlyRenderedNodes ?? [];

      return previousTickers?.filter((t): t is string => !!t) ?? [];
    }

    previouslyRenderedNodes.current = currentlyRenderedNodes ?? [];
    return [];
  }, []);

  const initOnScrollEnd = useCallback(
    (gridApi: GridApi | null, callback: (visibleData: VisibleData, event: BodyScrollEndEvent) => void) => {
      previouslyRenderedNodes.current = gridApi?.getRenderedNodes() ?? [];
      if (!scrollListener?.current) {
        scrollListener.current =
          gridApi?.addEventListener('bodyScrollEnd', (event: BodyScrollEndEvent) => {
            callback(
              {
                ...getVisibleData(gridApi),
                removedSymbols: event?.direction === 'vertical' ? getRemovedSymbols(gridApi) : [],
              },
              event,
            );
          }) ?? null;
      }
      return scrollListener.current;
    },
    [getVisibleData, getRemovedSymbols],
  );

  const onTransactionApplied = useCallback((gridApi: GridApi | null, callback: () => void) => {
    if (!gridApi || !callback) return;

    const onTransactionAppliedCallback = () => {
      startTransition(callback);
      previouslyRenderedNodes.current = gridApi?.getRenderedNodes() ?? [];
      gridApi.removeEventListener('asyncTransactionsFlushed', onTransactionAppliedCallback);
    };

    gridApi.addEventListener('asyncTransactionsFlushed', onTransactionAppliedCallback);
  }, []);

  const filterVisibleSymbols = useCallback(
    (gridApi: GridApi | null, symbols: string[]): string[] => {
      const { symbols: visibleSymbols } = getVisibleData(gridApi);
      return symbols?.filter(symbol => visibleSymbols.includes(symbol));
    },
    [getVisibleData],
  );

  return {
    filterVisibleSymbols,
    getRemovedSymbols,
    getVisibleData,
    initOnScrollEnd,
    onTransactionApplied,
  };
};
